import { useEffect, useRef } from 'react';

const API_URL = process.env.REACT_APP_API_URL;

const IDLE_TIMEOUT_MS = 25 * 60 * 1000;        // 25 minutes idle timeout (less than 30min refresh token expiry)
const REFRESH_INTERVAL_MS = 20 * 60 * 1000; // refresh every 20 min

const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'keyup', 'click', 'scroll', 'touchstart', 'input', 'change', 'focus', 'mousedown'];

export const useIdleTimer = (onIdle: () => void) => {
  // Use refs to avoid stale closures and prevent useEffect re-runs
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const isIdle = useRef(false);
  const onIdleRef = useRef(onIdle);

  // Keep onIdleRef current without causing re-renders
  useEffect(() => {
    onIdleRef.current = onIdle;
  });

  useEffect(() => {
    const loginToken = localStorage.getItem('loginToken');
    if (!loginToken) return;

    const doRefreshToken = async () => {
      const token = localStorage.getItem('refreshToken');
      if (!token || isIdle.current) return;
      try {
        const res = await fetch(`${API_URL}/auth/refreshToken`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: token }),
        });

        // 400/401 → refreshToken expired or invalid → logout immediately
        if (!res.ok) {
          console.warn(`[IdleTimer] Refresh token failed with status ${res.status} — logging out`);
          if (refreshTimer.current) clearInterval(refreshTimer.current);
          if (idleTimer.current) clearTimeout(idleTimer.current);
          localStorage.clear();
          window.location.href = '/';
          return;
        }

        const data = await res.json();
        if (data?.loginToken) {
          localStorage.setItem('loginToken', data.loginToken);
          if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
          console.log('[IdleTimer] Token refreshed successfully');
        } else {
          // Response OK but no token — also logout
          console.warn('[IdleTimer] Refresh response missing loginToken — logging out');
          if (refreshTimer.current) clearInterval(refreshTimer.current);
          localStorage.clear();
          window.location.href = '/';
        }
      } catch (err) {
        console.error('[IdleTimer] Token refresh failed (network error):', err);
        // Network error — don't logout, will retry on next interval
      }
    };

    const resetIdleTimer = () => {
      if (isIdle.current) return;
      if (idleTimer.current) clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(() => {
        isIdle.current = true;
        if (refreshTimer.current) clearInterval(refreshTimer.current);
        console.log('[IdleTimer] User is idle — showing modal');
        onIdleRef.current();
      }, IDLE_TIMEOUT_MS);
    };

    // Start timers
    resetIdleTimer();
    refreshTimer.current = setInterval(doRefreshToken, REFRESH_INTERVAL_MS);

    // Attach listeners — use capture:true to catch events inside modals/portals
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, resetIdleTimer, { capture: true, passive: true }));

    return () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      if (refreshTimer.current) clearInterval(refreshTimer.current);
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, resetIdleTimer, { capture: true }));
    };
  }, []); // empty deps — runs once on mount only
};
